<template>
  <div class="vuestic-modal">
    <transition name="modal" :duration="duration">
      <div v-show="show" class="modal-container">
        <div class="modal" @click.self="clickMask">
          <div class="modal-dialog modal-fullscreen">
            <div class="modal-content" ref="modal-body">
              <!--Header-->
              <div v-if="isDocumentOpened" class="modal-header">
                <slot name="header">
                  <div class="modal-title">
                    <slot name="title">
                      {{ this.header }}
                    </slot>
                  </div>
                  <i
                    class="ion ion-md-close close-modal"
                    v-if="closeIconShown"
                    @click.prevent="cancel"
                  />
                </slot>
              </div>

              <!--Container-->
              <div v-if="isDocumentOpened" class="modal-body va-row" :style="viewerStyles">
                <!-- Viewer -->
                <div class="img-container" :class="this.autoRotate ? `rotate-${this.rotation}` : ''" v-show="viewerOptionsReady">
                  <viewer
                    ref="viewer"
                    rebuild
                    :options="viewerOptions"
                    :images="[this.currentDocumentFile]"
                    class="viewer"
                  >
                    <template slot-scope="scope">
                      <img
                        class="viewer-image"
                        :class="scope.rotation"
                        v-for="src in scope.images"
                        width="28%"
                        height="28%"
                        :src="src"
                        :key="src"
                      />
                    </template>
                  </viewer>
                </div>
                <span aria-hidden="true" class="icon ion ion-ios-arrow-back arrow back" @click="prevDocument"></span>
                <span aria-hidden="true" class="icon ion ion-ios-arrow-forward arrow forward" :class="isSidebarVisible ? 'moved':''" @click="nextDocument"></span>
                <span aria-hidden="true" :class="!isSidebarVisible ? 'entypo-menu' : 'entypo-right moved'" class="icon entypo top-left-icon" @click="isSidebarVisible = !isSidebarVisible"></span>
                <!-- Sidebar -->
                <transition name="slide">
                  <div class="slidein" v-if="isSidebarVisible">
                    <div class="flex">
                      <div class="form-group with-icon-left">
                        <div class="input-group">
                          <input name="docName" required v-model="title" />
                          <i class="entypo entypo-doc-text icon-left input-icon"></i>
                          <label class="control-label" for="docName">{{
                            $t(`document.docName`)
                          }}</label>
                          <i class="bar"></i>
                        </div>
                      </div>

                      <div class="form-group">
                        <div class="input-group">
                          <vuestic-date-picker
                            :value="documentDate"
                            @on-change="this.changeDate"
                            :config="dateConfig"/>
                          <label class="control-label" for="date-picker-basic">{{ $t("dateTimePicker.from") }}</label>
                          <i class="bar"></i>
                        </div>
                      </div>

                      <h6 class="mb-4 mt-lg-5">
                        {{
                          $t("document.dateRecieved", { date: this.recievedAt })
                        }}
                      </h6>

                      <div class="form-group mb-2">
                        <div class="input-group" id="textarea-wrapper">
                          <textarea
                            v-model="notes"
                            class="pl-2 pt-3"
                            type="text"
                            id="notes-textarea"
                            required
                          ></textarea>
                          <label
                            class="control-label pl-2"
                            id="notes-textarea-label"
                            for="simple-textarea"
                            >{{ "document.notes" | translate }}</label
                          >
                          <i class="bar m-2"></i>
                        </div>
                      </div>

                      <div class="mb-5">
                        <div class="flex text-center">
                          <button
                            @click="print()"
                            class="btn btn-primary btn-micro"
                          >
                            <span
                              class="fa fa-print mr-1"
                              style="font-size: 18px"
                            ></span
                            >{{ "actions.print" | translate }}
                          </button>
                        </div>
                        <div class="flex va-row print-row">
                          <vuestic-checkbox
                            class="xs8"
                            :label="$t('document.isPrinted')"
                            v-model="isPrinted"
                          />
                        </div>
                        <vuestic-checkbox :label="$t('document.isOriginal')" v-model="hasOriginal"/>
                      </div>
                      <vuestic-simple-select
                        autocomplete="off"
                        :label="'document.reasons' | translate"
                        :options="reasons"
                        option-key="description"
                        v-model="invoiceType"
                        @input="onReasonChange"
                      />
                    </div>
                  </div>
                </transition>
              </div>
              <!--Footer-->
              <div class="modal-footer">
                <slot name="footer">
                  <button
                    type="button"
                    v-if="!noButtons"
                    :class="okClass"
                    @click="ok"
                    :disabled="okDisabled"
                  >
                    {{ okText }}
                  </button>
                </slot>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-backdrop"></div>
      </div>
    </transition>
  </div>
</template>
<script>
import { debounce } from 'lodash'
import { host } from '../../services/http'
import VuesticCheckbox from '../../vuestic-theme/vuestic-components/vuestic-checkbox/VuesticCheckbox'
import Viewer from 'v-viewer/src/component.vue'
import { ImgViewerDefaultOptions } from '../../utils.js'
import { mapGetters } from 'vuex'
import { getUserDate, getServerDate } from '../../helpers'

export default {
  name: 'company-document-preview',
  props: {
    startDocumentIndex: {
      type: Number,
    },

    transition: {
      type: String,
      default: 'modal',
    },
    small: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    force: {
      type: Boolean,
      default: false,
    },
    okText: {
      type: String,
      default: 'CONFIRM',
    },
    cancelText: {
      type: String,
      default: 'CANCEL',
    },
    okClass: {
      type: String,
      default: 'btn btn-primary',
    },
    cancelClass: {
      type: String,
      default: 'btn btn-secondary',
    },
    closeIconShown: {
      type: Boolean,
      default: true,
    },
    okDisabled: {
      type: Boolean,
      default: false,
    },
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    noButtons: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VuesticCheckbox,
    Viewer,
  },
  data () {
    return {
      docChanging: true,
      show: false,
      duration: 500,
      viewerOptions: null,
      dateConfig: {
        dateFormat: 'd-m-Y',
        locale: { firstDayOfWeek: 1 },
      },
      isSidebarVisible: false,
      autoRotate: true,
      viewerOptionsReady: false,
      viewerStyles: {
        minHeight: 0,
        minWidth: 0
      }
    }
  },
  updated: function () {
    this.$nextTick(function () {
      // Code that will run only after the
      // entire view has been re-rendered
      this.docChanging = false
      if (!this.viewerOptionsReady) {
        let fixedWidth = this.$refs['modal-body']
          ? Math.floor(this.$refs['modal-body'].clientWidth * 0.95)
          : 0
        let height = document.body.clientHeight
        let fixedHeight = height * 0.8
        this.viewerOptions = {
          ...ImgViewerDefaultOptions,
          minHeight: fixedHeight,
          minWidth: fixedWidth,
        }
        this.viewerOptionsReady = true
        this.viewerStyles = { ...this.viewerStyles, minHeight: `${fixedHeight + 80}px`, minWidth: `${fixedWidth}px` }
      }
    })
  },

  computed: {
    ...mapGetters('document', {
      currentDocument: 'openedDocument',
      isDocumentOpened: 'isOpenedDocument',
    }),
    ...mapGetters('app', ['dateFormat', 'serverDateFormat']),

    header () {
      return this.notes !== '' ? this.notes : this.$t('document.previewTitle', {
        number: this.currentDocument.title,
        date: this.documentDate,
      })
    },

    reasons () {
      return this.$store.getters['app/reasons'].map((r) => {
        return { id: r.id, description: this.$t(r.description) }
      })
    },

    currentDocumentFile () {
      return this.currentDocument ? `${host}${this.currentDocument.file}` : ''
    },

    title: {
      get () {
        return this.currentDocument.title || this.$t('document.docNoName')
      },
      set: debounce(function (newValue) {
        if (!this.docChanging) {
          this.changeDocument({ title: newValue })
        }
      }, 1000),
    },
    documentDate: {
      get: function () {
        return getUserDate(this.currentDocument.documentDate)
      },
    },
    recievedAt () {
      return getUserDate(this.currentDocument.createdAt)
    },
    notes: {
      get: function () {
        return this.currentDocument.notes
      },
      set: debounce(function (newValue) {
        this.changeDocument({ notes: newValue })
      }, 1000),
    },
    invoiceType: {
      get: function () {
        return this.reasons.find(r => r.id === this.currentDocument.invoiceType)
      },
      set: function (newValue) {
        if (!this.docChanging) {
          this.changeDocument({ invoiceType: newValue.id })
        }
      },
    },
    isPrinted: {
      get: function () {
        return !!this.currentDocument.isPrinted
      },
      set: function (newValue) {
        if (!this.docChanging) {
          this.changeDocument({ isPrinted: !!newValue })
        }
      },
    },
    hasOriginal: {
      get: function () {
        return !!this.currentDocument.hasOriginal
      },
      set: function (newValue) {
        if (!this.docChanging) {
          this.changeDocument({ hasOriginal: !!newValue })
        }
      },
    },
    rotation: {
      get () {
        const filePath = this.currentDocument.file.split('/')
        const fileName = filePath[filePath.length - 1].split('.')[0]

        var angle = localStorage.getItem(`image-rotation/${fileName}`) || 0
        return (angle >= 0 ? angle : 360 - (-angle % 360)) % 360 // normalize angle
      },
      set (newValue) {
        const filePath = this.currentDocument.file.split('/')
        const fileName = filePath[filePath.length - 1].split('.')[0]
        localStorage.setItem(`image-rotation/${fileName}`, newValue)
      },
    },
  },
  created () {
    if (this.show) {
      document.body.className += ' modal-open'
    }
  },
  beforeDestroy () {
    document.body.className = document.body.className.replace(
      /\s?modal-open/,
      ''
    )
  },
  watch: {
    show (value) {
      if (value) {
        document.body.className += ' modal-open'
        this.viewerOptionsReady = false
      } else {
        window.setTimeout(() => {
          document.body.className = document.body.className.replace(
            /\s?modal-open/,
            ''
          )
        }, this.duration)
        this.$refs.viewer.$viewer.body.removeEventListener(
          'click',
          this.rotationHandler
        )
      }
    },
    currentDocument (value) {
      if (value) {
        if (!this.show) {
          this.open()
        }
        this.autoRotate = true
      } else {
        this.close()
      }
    },
  },
  methods: {
    toggleSidebar () {
      this.isSidebarVisible = !this.isSidebarVisible
    },

    prevDocument () {
      this.$store.dispatch('document/openPrevDocument').then(() => {
        this.docChanging = true
      })
    },

    nextDocument () {
      this.$store.dispatch('document/openNextDocument').then(() => {
        this.docChanging = true
      })
    },

    onReasonChange (value) {
      if (!value) {
        this.invoiceType = this.reasons.find((r) => r.description === this.$t('document.taxEvents.other'))
      }
    },

    listenKeyUp (event) {
      if (event.key === 'Escape') {
        this.cancel()
      }
    },
    clickMask () {
      if (!this.force) {
        this.cancel()
      }
    },
    print () {
      let images = document.getElementsByClassName('viewer-image')
      for (var i = 0; i < images.length; i++) {
        images[i].removeAttribute('width')
        images[i].removeAttribute('height')
      }
      window.print()

      this.$nextTick(() => {
        for (var i = 0; i < images.length; i++) {
          images[i].setAttribute('width', '60%')
          images[i].setAttribute('height', '60%')
        }
      })
    },
    ok () {
      this.$emit('ok')
      this.show = false
      window.removeEventListener('keyup', this.listenKeyUp)
      this.$store.dispatch('document/saveAndCloseDocument')
    },
    cancel () {
      this.$emit('cancel')
      this.show = false
      window.removeEventListener('keyup', this.listenKeyUp)
      this.$store.dispatch('document/closeDocument')
    },

    open () {
      this.show = true
      window.addEventListener('keyup', this.listenKeyUp)
    },
    close () {
      this.show = false
      this.$emit('close')
      window.removeEventListener('keyup', this.listenKeyUp)
    },
    changeDocument (updates) {
      this.$store.dispatch('document/updateDocument', {
        docId: this.currentDocument.id,
        updates,
      })
    },
    changeDate (newDate) {
      if (!this.docChanging) {
        this.changeDocument({ documentDate: getServerDate(newDate[0]) })
      }
    },
    rotationHandler (e) {
      if (e.target.className.indexOf('viewer-rotate-right') > -1 || e.target.className.indexOf('viewer-rotate-left') > -1) {
        let toRorate = this.$refs.viewer.$viewer.imageData.rotate
        if (this.autoRotate) {
          toRorate += this.rotation
        }
        this.autoRotate = false
        this.rotation = toRorate
        this.$refs.viewer.$viewer.rotateTo(toRorate)
      }
    }
  },
}
</script>

<style lang="scss">
//Modals
$modal-header-padding-x: $widget-padding;
$modal-header-padding-y: 0;
$modal-header-height: $widget-header-height;
$modal-header-border: $widget-header-border;
$modal-content-border-width: 0;
$modal-content-border-radius: 0;
$modal-inner-padding: 25px;
$modal-footer-btns-padding-bottom: 20px;
$modal-footer-btns-margin-x: 10px;
$modal-md: 650px;
$modal-lg: 1050px;

.vuestic-modal {
  height: 0;
  width: 0;

  // For Transitioning
  .modal {
    display: block;
  }

  .modal-body {
    position: relative;
    margin: auto 0 !important;
    padding: 0 !important;
  }

  .modal-dialog,
  .modal-backdrop {
    transition: all 0.5s ease;
  }

  .modal-enter .modal-dialog,
  .modal-leave-active .modal-dialog {
    opacity: 0;
    transform: translateY(-30%);
  }

  .modal-enter .modal-backdrop,
  .modal-leave-active .modal-backdrop {
    opacity: 0;
  }

  .modal-backdrop {
    opacity: 0.5;
  }

  //Modal styles

  .modal-header {
    height: $modal-header-height;
    padding: $modal-header-padding-y $modal-header-padding-x;
    border-bottom: $modal-header-border;
    font-size: $font-size-larger;
    display: flex;
    align-items: center;
  }

  .close-modal {
    margin-left: 1rem;
    font-size: $font-size-large;
    line-height: $font-size-large;
    cursor: pointer;
  }

  .modal-content {
    border-radius: $modal-content-border-radius;
  }

  .modal-footer {
    justify-content: center;
    padding: 0 $modal-inner-padding;
    padding-bottom: calc(
      #{$modal-inner-padding} - #{$modal-footer-btns-padding-bottom}
    );
    flex-wrap: wrap;

    .btn {
      margin: 0 $modal-footer-btns-margin-x $modal-footer-btns-padding-bottom
        $modal-footer-btns-margin-x;
    }
  }

  .modal-dialog {
    box-shadow: $modal-content-box-shadow-sm-up;
  }

  .viewer {
    // display: none;
    visibility: hidden;
  }

  .modal-fullscreen {
    max-width: 93% !important;
  }

  .slidein {
    max-width: 600px;
    padding: 1em 2em;
    position: absolute;
    z-index: 100;
    top: 0;
    right: 0;
    background: #dddddd63;
    height: 100%;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.5);
    transition: all 0.5s ease-in-out;
  }

  /* before the element is shown, start off the screen to the right */
  .slide-enter,
  .slide-leave-active {
    right: -100%;
  }

  .moved{
    right: 18% !important;
    transition: all 0.5s ease-in-out;
  }

  .entypo-menu, .arrow.forward{
    right: 0%;
    transition: all 0.5s ease-in-out;
  }

  .close-btn {
    border: none;
    font-weight: bold;
    font-size: 2em;
    background: transparent;
    position: absolute;
    top: 0;
    left: 0;
    padding: 0.5em;
  }

  .sidebar {
    border-left: 1px solid #b3b3b3;

    &.hidden {
      transform: translateX(100%);
      // visibility: hidden;
      display: none;
      opacity: 0;
      transition: transform ease-out 0.3s, visibility 0s linear 300ms,
        opacity 300ms;
    }

    &.open {
      // visibility: visible;
      display: unset;
      opacity: 1;
      transform: translateX(0);
      transition: transform ease-in 0.3s, visibility 0s linear 0s, opacity 300ms;
    }
  }

  .top-left-icon {
    position: absolute;
    top: 0%;
    right: 0%;
    margin-right: 0.6rem;
  }

  .img-container {
    &.rotate-0 .viewer-canvas img {
      transform: rotate(0deg) !important;
    }
    &.rotate-90 .viewer-canvas img {
      transform: rotate(90deg) !important;
    }

    &.rotate-180 .viewer-canvas img {
      transform: rotate(180deg) !important;
    }

    &.rotate-270 .viewer-canvas img {
      transform: rotate(270deg) !important;
    }
  }

  #notes-textarea {
    min-height: 110px;
  }

  #textarea-wrapper {
    border: 1px solid $light-pink;
    border-radius: 8px;
  }

  .print-btn {
    padding: 0.625rem 0.8rem !important;
  }

  .print-row {
    align-items: center;
    display: flex;
    justify-content: center;

    // & .vuestic-checkbox {
    //   margin-bottom: 0 !important;
    //   border-right: 1px solid $gray-light;
    // }
  }

  .form-group textarea {
    &:focus ~ .control-label,
    &:valid ~ .control-label {
      top: 0.3rem;
    }
  }

  .arrow {
    position: absolute;
    top: 50%;
    font-size: 30px;
    z-index: 1000;

    &.forward {
      right: 1%;
    }

    &.back{
      left: 1%;
    }

    &:hover {
      cursor: pointer;
    }
  }

  .icon{
    font-size:30px;
  }

  @media print {
    body * {
      display: none;
      background-color: red;
    }

    body {
      display: none;
      background-color: red;
    }

    .viewer {
      visibility: visible;
      background-color: white;
      height: 100%;
      width: 100%;
      position: fixed;
      top: 0;
      left: 0;
      margin: 0;
      padding: 15px;
      font-size: 14px;
      line-height: 18px;
      z-index: 9999;
    }
  }
}
</style>
